import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Button,
  ButtonGroup,
  Link,
  SidePanel,
  Snack,
  Snackbar,
  Spinner,
  Text,
  Tooltip,
} from '@nike/eds'
import { useForm } from 'react-hook-form'
import { DatePicker, InputGroup, TextArea, TextField } from '../../../common/components/form'
import { useCarrierHubList } from '../../../common/hooks/useCarrierHubList'
import { CarrierHubTreeDropdown } from '../../../common/components/carrierhubtreedropdown/CarrierHubTreeDropdown'
import { RebookingInitiateFilter } from '../../../services/ShipmentService'
import {
  ApplicationSettings,
  updateUserPreferences,
  ApplicationFilter,
} from '../../../services/UserPreferencesService'
import { useOktaAuth } from '@okta/okta-react'
import {
  mapStringToArrayOrUndefined,
  valuesAreAllNumbers,
} from '../../../common/utils/datautils/utils'
import '../../../common/styles/FilterForm.css'

const emptyFormData = {
  carrierHubs: [],
  packlists: '',
  shipToCodes: '',
  iDPPlannedFromTimestamp: '',
  iDPPlannedToTimestamp: '',
  trailer: '',
  salesOrders: '',
  deliveryDocs: '',
}

const useFilterForm = (isOpen, initData: RebookingInitiateFilter) => {
  const _formData = useMemo(() => {
    const {
      carrierHubs = emptyFormData.carrierHubs,
      packlists = [],
      shipToCodes = [],
      iDPPlannedFromTimestamp = emptyFormData.iDPPlannedFromTimestamp,
      iDPPlannedToTimestamp = emptyFormData.iDPPlannedToTimestamp,
      trailer = emptyFormData.trailer,
      salesOrders = [],
      deliveryDocs = [],
    } = initData ?? {}
    return isOpen
      ? {
          carrierHubs,
          packlists: packlists.join('\n') ?? emptyFormData.packlists,
          shipToCodes: shipToCodes.join('\n') ?? emptyFormData.shipToCodes,
          iDPPlannedFromTimestamp,
          iDPPlannedToTimestamp,
          trailer,
          salesOrders: salesOrders.join('\n') ?? emptyFormData.salesOrders,
          deliveryDocs: deliveryDocs.join('\n') ?? emptyFormData.deliveryDocs,
        }
      : emptyFormData
  }, [isOpen, initData])

  const { reset, resetField, getValues, ...restForm } = useForm({
    defaultValues: _formData,
    mode: 'all',
  })

  useEffect(() => {
    // refresh the form state whenever we open it
    if (isOpen) {
      reset(_formData)
    }
  }, [isOpen, reset, _formData])

  // extra utility functions...
  const clear = useCallback(() => reset(emptyFormData), [reset])

  return {
    ...restForm,
    reset,
    clear,
    resetField,
    getValues,
  }
}

export interface FilterProps {
  currentFilter: RebookingInitiateFilter
  onClickFilter: (formData: RebookingInitiateFilter) => void
  expand: boolean
  onCloseSidePanel: () => void
}

export const InitiateFilterForm = ({
  currentFilter: initData,
  onClickFilter,
  expand = false,
  onCloseSidePanel,
}: FilterProps): JSX.Element => {
  const { data: carrierHubData, isSuccess: isCarrierHubCallSuccess } = useCarrierHubList()
  const { control, handleSubmit, clear } = useFilterForm(expand, initData)
  const { authState } = useOktaAuth()
  const [isSaving, setIsSaving] = useState(false)
  const [appliedFilterSnackOpen, setAppliedFilterSnackOpen] = useState(false)

  const createFilter = data => {
    return {
      carrierHubs: data.carrierHubs?.length ? data.carrierHubs : undefined,
      packlists: mapStringToArrayOrUndefined(data.packlists),
      shipToCodes: mapStringToArrayOrUndefined(data.shipToCodes),
      iDPPlannedFromTimestamp: data.iDPPlannedFromTimestamp
        ? data.iDPPlannedFromTimestamp
        : undefined,
      iDPPlannedToTimestamp: data.iDPPlannedToTimestamp ? data.iDPPlannedToTimestamp : undefined,
      trailer: data.trailer ? data.trailer : undefined,
      salesOrders: mapStringToArrayOrUndefined(data.salesOrders),
      deliveryDocs: mapStringToArrayOrUndefined(data.deliveryDocs),
    }
  }

  const applyFilters = (data, setDefault: boolean) => {
    if (setDefault) {
      handlePostFilter(data)
    }
    // do some data sanitization before updating the higher state
    onClickFilter(createFilter(data))
    onCloseSidePanel()
  }

  const closeAndResetFilters = () => {
    onCloseSidePanel()
  }

  const handlePostFilter = async data => {
    setIsSaving(true)
    const defaultFilter: ApplicationFilter = {
      name: 'default',
      default: true,
      filter: createFilter(data),
    }
    const applicationSettings: ApplicationSettings = {
      filters: [defaultFilter],
    }
    await updateUserPreferences(authState, applicationSettings, 'initiate', 'filter')
    setIsSaving(false)
    setAppliedFilterSnackOpen(true)
    setTimeout(() => {
      setAppliedFilterSnackOpen(false)
    }, 3000)
  }

  const formId = 'shipmentInitiateFilterForm'

  return (
    <>
      <SidePanel
        onDismiss={closeAndResetFilters}
        hasScrim={false}
        isOpen={expand}
        headerSlot={
          <>
            <Text font="title-2" as="h2">
              Filter
            </Text>
            <span className="eds-type--body-2">
              <Link as="button" variant="secondary" onClick={clear}>
                Clear All
              </Link>
            </span>
          </>
        }
        footerSlot={
          <ButtonGroup>
            <Button variant="primary" type="submit" size="small" form={formId}>
              Apply
            </Button>

            <Tooltip
              bodySlot={<Text>Save as default filter and apply</Text>}
              isDark={true}
              placement={'right'}
            >
              <Button
                form={formId}
                variant="secondary"
                type="submit"
                size="small"
                style={{ marginBottom: 'var(--eds-space-4)' }}
                onClick={async () => {
                  await handleSubmit(data => applyFilters(data, true))()
                }}
                disabled={isSaving}
              >
                {isSaving && <Spinner />}
                {isSaving ? 'Saving...' : 'Save as default'}
              </Button>
            </Tooltip>
          </ButtonGroup>
        }
      >
        <form
          id={formId}
          className="filter-form"
          onSubmit={handleSubmit(data => applyFilters(data, false))}
        >
          <InputGroup label="Carrier / Hub" clearable={false} withDivider={false}>
            <CarrierHubTreeDropdown
              name="carrierHubs"
              control={control}
              carrierHubs={isCarrierHubCallSuccess && carrierHubData ? carrierHubData : []}
              showPartiallySelected={true}
            />
          </InputGroup>

          <InputGroup label="Packlist" clearable={false} withDivider={false}>
            <TextArea
              name="packlists"
              control={control}
              resize="vertical"
              // @ts-ignore
              style={{ padding: 'var(--eds-space-4)' }}
              label="Packlist"
              rules={{
                validate: input =>
                  valuesAreAllNumbers(input) || 'Packlist can only contain numeric values',
              }}
            />
          </InputGroup>

          <InputGroup label="Ship To Number" clearable={false} withDivider={false}>
            <TextArea
              name="shipToCodes"
              control={control}
              resize="vertical"
              // @ts-ignore
              style={{ padding: 'var(--eds-space-4)' }}
              label="Ship To Number"
              rules={{
                validate: input =>
                  valuesAreAllNumbers(input) || 'Ship To Number can only contain numeric values',
              }}
            />
          </InputGroup>

          <DatePicker
            control={control}
            from={{
              name: 'iDPPlannedFromTimestamp',
            }}
            to={{
              name: 'iDPPlannedToTimestamp',
            }}
            title={'Original IDP Date'}
          />

          <InputGroup label="Trailer" clearable={false} withDivider={false}>
            <TextField
              name="trailer"
              control={control}
              label="Trailer"
              style={{ padding: 'var(--eds-space-4)' }}
            />
          </InputGroup>

          <InputGroup label="Sales Order" clearable={false} withDivider={false}>
            <TextArea
              name="salesOrders"
              control={control}
              resize="vertical"
              // @ts-ignore
              style={{ padding: 'var(--eds-space-4)' }}
              label="Sales Order"
              rules={{
                validate: input =>
                  valuesAreAllNumbers(input) || 'Sales orders can only contain numeric values',
              }}
            />
          </InputGroup>

          <InputGroup label="Delivery Note" clearable={false} withDivider={false}>
            <TextArea
              name="deliveryDocs"
              control={control}
              resize="vertical"
              // @ts-ignore
              style={{ padding: 'var(--eds-space-4)' }}
              label="Delivery Note"
              rules={{
                validate: input =>
                  valuesAreAllNumbers(input) || 'Delivery Note can only contain numeric values',
              }}
            />
          </InputGroup>
        </form>
      </SidePanel>

      <Snackbar>
        {appliedFilterSnackOpen && (
          <Snack id="1" status="success" onDismiss={id => setAppliedFilterSnackOpen(false)}>
            Filter saved!
          </Snack>
        )}
      </Snackbar>
    </>
  )
}
